/**
 * Restricting hierarchy dropdowns to the nodes the caller may actually pick.
 *
 * The shared `/mediworks/*` endpoints are used by other applications, so they
 * are deliberately NOT scoped server-side — the narrowing happens here, from
 * the caller's seat. That makes this a UX boundary rather than a security one:
 * the reads that matter are clamped again by the backend's scope guard.
 *
 * The rule: at a level where the caller holds a seat value, only that node is
 * offered; below their seat everything is offered, because their whole subtree
 * is theirs to manage.
 */

/** Broadest first — the index of a level is its depth. */
const LEVEL_ORDER = ["client", "employer", "group", "division", "location"] as const;

export type HierarchyLevel = (typeof LEVEL_ORDER)[number];

/**
 * The caller's own position in the hierarchy, read from their staff mapping
 * (carried on the token, completed by `/auth/me`).
 */
export interface SeatScope {
  /** Platform staff: no seat by design, and unrestricted. */
  isPlatform: boolean;
  /** Holds at least one hierarchy id, so their view must be narrowed to it. */
  isSeated: boolean;
  /**
   * Whether the seat is resolved at all. False on the first render pass, where
   * `isSeated` also reads false — without this the two are indistinguishable
   * and every dropdown briefly offers the whole hierarchy.
   */
  isKnown: boolean;
  clientId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
  /** Whether this user may write commission, per the backend's writable features. */
  canWriteCommission: boolean;
}

/** Parent ids a row must sit under to be offered. An undefined level is unconstrained. */
export interface ScopeAncestors {
  clientId?: number | string;
  employerId?: number | string;
  groupId?: number | string;
  divisionId?: number | string;
}

/** A hierarchy row, carrying whichever parent ids its level has. */
interface HierarchyRow {
  clientId?: number | string;
  employerId?: number | string;
  groupId?: number | string;
  divisionId?: number | string;
}

function pinnedFor(seat: SeatScope, level: HierarchyLevel): number | undefined {
  switch (level) {
    case "client":
      return seat.clientId;
    case "employer":
      return seat.employerId;
    case "group":
      return seat.groupId;
    case "division":
      return seat.divisionId;
    case "location":
      return seat.locationId;
    default:
      return undefined;
  }
}

/** Depth of the deepest level carrying a value; `-1` when the seat has none. */
function deepestSeatedIndex(seat: SeatScope): number {
  let deepest = -1;
  LEVEL_ORDER.forEach((level, index) => {
    if (pinnedFor(seat, level) != null) deepest = index;
  });
  return deepest;
}

/**
 * True once the seat's chain has no HOLES — every level from client down to the
 * deepest id we know about is populated.
 *
 * A seat arrives in two parts (the token, then `/auth/me` filling the gaps), so
 * it is briefly partial: a group-seated caller can have `groupId` while
 * `clientId` is still unknown. Anything that defaults a dropdown off the seat
 * has to wait for this, because a level can only be defaulted together with its
 * ancestors — the child queries take them as parameters.
 */
export function isChainSettled(seat: SeatScope): boolean {
  const chain = [seat.clientId, seat.employerId, seat.groupId, seat.divisionId, seat.locationId];
  const deepest = chain.reduce<number>((acc, id, index) => (id != null ? index : acc), -1);
  if (deepest < 0) return false;
  return chain.slice(0, deepest + 1).every((id) => id != null);
}

/**
 * Whether this caller must be shown nothing — the fail-closed case.
 *
 * True while the seat is unresolved (ask nothing rather than ask unscoped), and
 * true for a NON-platform caller holding no mapping at all. That second case
 * otherwise shares the `!isSeated` branch with platform callers and reads as
 * unrestricted, handing the whole tenant to a user whose mapping row is missing
 * or holds only `0`/`-1` placeholders. Absence of a mapping is not permission:
 * platform staff are identified positively, and everyone else needs a seat.
 *
 * Use it to skip list queries too, so the request is never sent.
 */
export function scopeDenied(seat: SeatScope): boolean {
  if (!seat.isKnown) return true;
  if (seat.isPlatform) return false;
  return !seat.isSeated;
}

/**
 * The options this caller may choose at `level`.
 *
 * @param seatKnown overrides whether the seat is resolved; pass it only to
 * force the behaviour in a test.
 */
export function scopeOptions<T extends { id: number | string }>(
  options: T[],
  seat: SeatScope,
  level: HierarchyLevel,
  seatKnown?: boolean
): T[] {
  if (!(seatKnown ?? seat.isKnown)) return [];
  if (scopeDenied(seat)) return [];
  if (!seat.isSeated) return options;

  const pinned = pinnedFor(seat, level);
  if (pinned == null) {
    // No id at a level ABOVE the deepest seat value is a gap in the mapping,
    // not freedom — offering everything there would show a division-seated
    // caller with a null groupId every group in the client. Withhold, so bad
    // mapping data fails visibly instead of leaking.
    if (LEVEL_ORDER.indexOf(level) < deepestSeatedIndex(seat)) return [];
    // Below the seat the whole subtree is theirs.
    return options;
  }
  // If the list has not loaded, or genuinely lacks the pinned node, empty beats
  // unscoped: offering the wrong nodes is the worse failure. Compared
  // numerically because ids arrive as strings from some endpoints.
  return options.filter((option) => Number(option.id) === pinned);
}

/**
 * Keeps only the rows under `ancestors` — the branch the caller is working in,
 * which is their SELECTION where they made one and their SEAT everywhere else.
 *
 * Cascades filter each level only once its parent is picked
 * (`employer ? groups.filter(...) : groups`), which looks harmless but isn't:
 * until the caller touches the filter bar there is no parent, so Group,
 * Division and Location offer every node in the tenant. `scopeOptions` does not
 * catch it — it pins the levels the caller sits AT, and these are the levels
 * below, where the subtree is legitimately theirs. Passing the seat as the
 * fallback ancestor keeps them open but within the caller's own branch.
 *
 * Rows missing a parent id are kept: some endpoints omit ancestor columns, and
 * dropping those rows would empty the dropdown rather than narrow it.
 */
export function underAncestors<T extends HierarchyRow>(rows: T[], ancestors: ScopeAncestors): T[] {
  const matches = (row: number | string | undefined, wanted: number | string | undefined) =>
    wanted == null || row == null || Number(row) === Number(wanted);
  return rows.filter(
    (row) =>
      matches(row.clientId, ancestors.clientId) &&
      matches(row.employerId, ancestors.employerId) &&
      matches(row.groupId, ancestors.groupId) &&
      matches(row.divisionId, ancestors.divisionId)
  );
}

/** What the caller has picked in the cascade so far. */
export interface HierarchySelection {
  clientId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
}

/** A cascade's option lists, broadest level first. */
export interface HierarchyLists<
  C extends { id: number | string },
  E extends { id: number | string } & HierarchyRow,
  G extends { id: number | string } & HierarchyRow,
  D extends { id: number | string } & HierarchyRow,
  L extends { id: number | string } & HierarchyRow,
> {
  clients: C[];
  employers: E[];
  groups: G[];
  divisions: D[];
  locations: L[];
}

/**
 * Narrows a whole cascade to what the caller may pick.
 *
 * Both narrowings are needed at every level. `scopeOptions` pins the levels the
 * caller sits AT, so an employer-seated user is offered only their own
 * employer. `underAncestors` confines the levels BELOW to the branch they are
 * working in — their selection where they made one, their seat everywhere else
 * — which is what stops a freshly loaded Groups dropdown from listing every
 * group in the tenant before they touch the filter bar.
 */
export function scopeHierarchy<
  C extends { id: number | string },
  E extends { id: number | string } & HierarchyRow,
  G extends { id: number | string } & HierarchyRow,
  D extends { id: number | string } & HierarchyRow,
  L extends { id: number | string } & HierarchyRow,
>(
  lists: HierarchyLists<C, E, G, D, L>,
  seat: SeatScope,
  selection: HierarchySelection = {}
): HierarchyLists<C, E, G, D, L> {
  const under = {
    clientId: selection.clientId ?? seat.clientId,
    employerId: selection.employerId ?? seat.employerId,
    groupId: selection.groupId ?? seat.groupId,
    divisionId: selection.divisionId ?? seat.divisionId,
  };

  return {
    clients: scopeOptions(lists.clients, seat, "client"),
    employers: scopeOptions(
      underAncestors(lists.employers, { clientId: under.clientId }),
      seat,
      "employer"
    ),
    groups: scopeOptions(
      underAncestors(lists.groups, { clientId: under.clientId, employerId: under.employerId }),
      seat,
      "group"
    ),
    divisions: scopeOptions(
      underAncestors(lists.divisions, {
        clientId: under.clientId,
        employerId: under.employerId,
        groupId: under.groupId,
      }),
      seat,
      "division"
    ),
    locations: scopeOptions(underAncestors(lists.locations, under), seat, "location"),
  };
}

/**
 * The deepest level the caller occupies — their FLOOR. That level and
 * everything under it is theirs; anything above is upstream. Undefined for a
 * platform caller, who has no floor.
 */
export function seatFloorLevel(seat: SeatScope): HierarchyLevel | undefined {
  if (!seat.isSeated) return undefined;
  const index = deepestSeatedIndex(seat);
  return index < 0 ? undefined : LEVEL_ORDER[index];
}

/**
 * Narrows a broadest-first list of levels to those at or below the caller's floor.
 *
 * Needed wherever the caller picks a LEVEL rather than a node: `scopeOptions`
 * clamps lists of ids, but a "scope level" selector has none, so a group-seated
 * caller could still choose "Employer — covers all locations" and author a rule
 * spanning the whole employer. Choosing it also hides the lower pickers, so the
 * wildcard is submitted without them ever seeing which nodes it covers.
 *
 * A caller seated ABOVE everything on offer keeps the full list — it is all
 * below them already. Never returns empty: if nothing matched, their mapping is
 * deeper than this selector models, and blanking the control only deadlocks the
 * form.
 */
export function levelsAtOrBelowSeat<T>(
  levels: readonly T[],
  seat: SeatScope,
  toHierarchyLevel: (level: T) => HierarchyLevel
): T[] {
  const floor = seatFloorLevel(seat);
  if (!floor) return [...levels];
  const floorIndex = LEVEL_ORDER.indexOf(floor);
  const allowed = levels.filter(
    (level) => LEVEL_ORDER.indexOf(toHierarchyLevel(level)) >= floorIndex
  );
  return allowed.length ? allowed : [...levels];
}
